import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Rating } from "@/components/ui/rating";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parse, isValid } from "date-fns";
import { ja } from "date-fns/locale";
import { CalendarIcon, MapPin, Utensils, Heart, Globe } from "lucide-react";
import { cn, compareDropdown } from "@/lib/utils";
import { deriveTasteFields } from "@/lib/rating";
import { loadAreaOptions, normalizeAreaRemote, type AreaPref } from "@/lib/areas";

interface AddRestaurantFormProps {
  onSubmit: (restaurant: any) => void;
  onCancel: () => void;
  initialData?: Partial<{
    id: string;
    name: string;
    cuisine: string;
    location: string;
    rating: number;
    visitDate: string | Date | undefined;
    isWishlist: boolean;
    notes: string;
    image: string;
    sourceUrl: string;
    tiktokUrl: string;
  }>;
  mode?: "create" | "edit";
}

export function AddRestaurantForm({ onSubmit, onCancel, initialData, mode = "create" }: AddRestaurantFormProps) {
  const [formData, setFormData] = useState({
    name: "",
    cuisine: "",
    location: "",
    prefecture: "",
    municipality: "",
    rating: 0,
    ratingTaste: 0,
    ratingTasteBalance: 0,
    ratingTasteIngredients: 0,
    ratingTasteTechnique: 0,
    ratingAmbience: 0,
    ratingService: 0,
    ratingCost: 0,
    visitDate: undefined as Date | undefined,
    isWishlist: false,
    notes: "",
    image: "",
    priceRange: "",
  });
  const [sourceId, setSourceId] = useState<string | undefined>(undefined);
  const [sourceUrl, setSourceUrl] = useState(""); // 保存用URL（レコードに保存）
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [parseUrl, setParseUrl] = useState("");   // 解析用URL（保存しない）
  const [parsing, setParsing] = useState(false);
  const [visitHistory, setVisitHistory] = useState<string[]>([]);
  const [areaOptions, setAreaOptions] = useState<AreaPref[]>([]);

  const tasteSummary = useMemo(() => {
    const derived = deriveTasteFields(formData as any);
    const direct = typeof formData.ratingTaste === "number" && formData.ratingTaste > 0 ? formData.ratingTaste : 0;
    const average = derived.ratingTaste ?? direct ?? 0;
    const fine = derived.ratingTasteFine ?? (average > 0 ? Math.round((average / 5) * 9 * 10) / 10 : 0);
    const grade = derived.ratingTasteGrade;
    return { average, fine, grade };
  }, [formData]);

  function sanitizeUrlInput(s: string): string {
    if (!s) return s;
    const m = s.match(/https?:\/\/[^\s]+/i);
    return m ? m[0] : s.trim();
  }

  function buildTabelogSearchUrl(): string {
    const name = (formData.name || '').trim();
    const area = [formData.prefecture, formData.municipality, formData.location]
      .map((s) => (s || '').trim())
      .find((s) => !!s);
    const q = ['site:tabelog.com', name, area].filter(Boolean).join(' ');
    return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
  }

  function openTabelogSearch() {
    const url = buildTabelogSearchUrl();
    try { window.open(url, '_blank', 'noopener'); } catch { location.href = url; }
  }

  // 新規作成時: 解析用URL入力→抽出後URLを保存用URLに自動反映
  useEffect(() => {
    if (!parseUrl) return;
    const cleaned = sanitizeUrlInput(parseUrl);
    // 新規作成時のみ、または保存用が空なら上書きしない配慮も可能だが、要件に合わせ create のみ反映
    if (mode === "create" && cleaned && cleaned !== sourceUrl) {
      setSourceUrl(cleaned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parseUrl]);

  useEffect(() => {
    let alive = true;
    loadAreaOptions()
      .then((prefs) => { if (alive) setAreaOptions(prefs); })
      .catch(() => { if (alive) setAreaOptions([]); });
    return () => { alive = false; };
  }, []);

  // 初期値の反映（編集時）
  useEffect(() => {
    if (initialData) {
      // visitDate は保存時に "yyyy年M月d日" の文字列。編集時は Date に直す。失敗したら undefined。
      const parsed = typeof initialData.visitDate === "string"
        ? parse(initialData.visitDate, "yyyy年M月d日", new Date(), { locale: ja as any })
        : (initialData.visitDate as Date | undefined);
      const fallbackTaste = typeof (initialData as any)?.ratingTaste === "number" && (initialData as any).ratingTaste > 0
        ? Number((initialData as any).ratingTaste)
        : 0;
      const rawTasteBalance = Number((initialData as any)?.ratingTasteBalance);
      const rawTasteIngredients = Number((initialData as any)?.ratingTasteIngredients);
      const rawTasteTechnique = Number((initialData as any)?.ratingTasteTechnique);
      const ratingTasteBalance = Number.isFinite(rawTasteBalance) && rawTasteBalance > 0 ? rawTasteBalance : fallbackTaste;
      const ratingTasteIngredients = Number.isFinite(rawTasteIngredients) && rawTasteIngredients > 0 ? rawTasteIngredients : fallbackTaste;
      const ratingTasteTechnique = Number.isFinite(rawTasteTechnique) && rawTasteTechnique > 0 ? rawTasteTechnique : fallbackTaste;
      setFormData({
        name: initialData.name ?? "",
        cuisine: initialData.cuisine ?? "",
        location: initialData.location ?? "",
        prefecture: (initialData as any).prefecture ?? "",
        municipality: (initialData as any).municipality ?? "",
        rating: initialData.rating ?? 0,
        ratingTaste: (initialData as any)?.ratingTaste ?? 0,
        ratingTasteBalance,
        ratingTasteIngredients,
        ratingTasteTechnique,
        ratingAmbience: (initialData as any)?.ratingAmbience ?? 0,
        ratingService: (initialData as any)?.ratingService ?? 0,
        ratingCost: (initialData as any)?.ratingCost ?? 0,
        visitDate: parsed && isValid(parsed as Date) ? (parsed as Date) : undefined,
        isWishlist: initialData.isWishlist ?? false,
        notes: initialData.notes ?? "",
        image: initialData.image ?? "",
        priceRange: initialData.priceRange ?? "",
      });
      if (initialData.sourceUrl) setSourceUrl(initialData.sourceUrl);
      if ((initialData as any).tiktokUrl) setTiktokUrl((initialData as any).tiktokUrl);
      if (initialData.id) setSourceId(initialData.id);
      // 訪問履歴（最大100件）: 既存配列があれば優先。無ければ visitDate を初期履歴として採用。
      const vh = Array.isArray((initialData as any).visitHistory)
        ? ((initialData as any).visitHistory as string[]).filter(Boolean)
        : [];
      const vd = typeof initialData.visitDate === 'string' ? initialData.visitDate : undefined;
      const uniq = Array.from(new Set([...(vh || []), ...(vd ? [vd] : [])]));
      setVisitHistory(uniq.slice(0, 100));
    }
  }, [initialData]);

  const cuisineTypes = [
  "イタリアン",
  "フレンチ",
  "寿司",
  "焼肉",
  "和食",
  "中華",
  "ラーメン",
  "そば",
  "うどん",
  "定食",
  "居酒屋",
  "駅弁",
  "ベトナム",
  "メキシカン",
  "インド",
  "タイ",
  "アメリカン",
  "韓国",
  "ベーカリー",
  "スペイン",
  "ギリシャ",
  "トルコ",
  "その他",
  // 追加
  "洋食",
  "カフェ",
];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const uniq = Array.from(new Set(visitHistory.filter(Boolean))).slice(0, 100);
    const latest = uniq[0];
    const tastePatch = deriveTasteFields(formData as any);
    const normalizedTaste = tastePatch.ratingTaste ?? (formData.ratingTaste && formData.ratingTaste > 0 ? formData.ratingTaste : 0);
    let normalizedArea = {
      prefecture: formData.prefecture,
      municipality: formData.municipality,
    };
    try {
      const area = await normalizeAreaRemote({
        name: formData.name,
        location: formData.location,
        prefecture: formData.prefecture,
        municipality: formData.municipality,
      });
      normalizedArea = {
        prefecture: area.prefecture || formData.prefecture,
        municipality: area.municipality || formData.municipality,
      };
    } catch {
      // 保存は継続する。地域マスタが落ちている場合だけ手入力値を使う。
    }
    onSubmit({
      ...formData,
      ...normalizedArea,
      ...tastePatch,
      ratingTaste: normalizedTaste,
      ratingTasteFine: tastePatch.ratingTasteFine ?? undefined,
      ratingTasteGrade: tastePatch.ratingTasteGrade ?? undefined,
      id: sourceId ?? undefined,
      sourceUrl,
      tiktokUrl,
      visitHistory: uniq,
      visitDate: (formData.visitDate && isValid(formData.visitDate))
        ? format(formData.visitDate, "yyyy年M月d日", { locale: ja as any })
        : (latest || undefined),
    });
  };

  const handleParseUrl = async () => {
    if (!parseUrl) return;
    const cleaned = sanitizeUrlInput(parseUrl);
    if (cleaned !== parseUrl) setParseUrl(cleaned);
    // 新規作成時は抽出後URLを保存用URLにも反映
    if (mode === "create" && cleaned && cleaned !== sourceUrl) {
      setSourceUrl(cleaned);
    }
    setParsing(true);
    try {
      const resp = await fetch("/api/extract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: cleaned }),
      });
      if (!resp.ok) {
        let detail: any = null;
        try { detail = await resp.json(); } catch {}
        const code = detail?.code as string | undefined;
        if (resp.status === 400) throw new Error("URLが正しくありません（http/https の完全な形式で入力してください）。");
        if (resp.status === 401 || code === "OPENAI_AUTH") throw new Error("解析エンジンの認証に失敗しました（管理者設定が必要です）。");
        if (resp.status === 429 || code === "OPENAI_RATE_LIMIT") throw new Error("解析が混み合っています。数十秒後にもう一度お試しください。");
        if (resp.status === 422 || code === "LLM_PARSE") throw new Error("記事内容から必要情報を抽出できませんでした（別のURLでお試しください）。");
        if (resp.status === 403) throw new Error("参照先サイトがアクセスを制限しています（HTTP 403）。しばらくして再試行するか、別のURLでお試しください。");
        if (resp.status === 502) throw new Error("参照先サイトの取得に失敗しました（URLの公開可否やアクセス可否をご確認ください）。");
        if (code === "NO_OPENAI_KEY") throw new Error("解析エンジンの設定が未完了です（管理者設定が必要です）。");
        throw new Error(detail?.error || `解析に失敗しました (HTTP ${resp.status})`);
      }
      const data = await resp.json();
      const detectedTikTokUrl = data.tiktokUrl || (/tiktok\.com|vt\.tiktok\.com|vm\.tiktok\.com/i.test(cleaned) ? cleaned : "");
      if (detectedTikTokUrl) setTiktokUrl(detectedTikTokUrl);
      const cuisine = cuisineTypes.includes(data.cuisine)
        ? data.cuisine
        : data.cuisine || formData.cuisine;
      setFormData((prev) => ({
        ...prev,
        name: data.name || prev.name,
        cuisine: cuisine || prev.cuisine,
        location: data.location || prev.location,
        prefecture: data.prefecture || prev.prefecture,
        municipality: data.municipality || prev.municipality,
        notes: data.notes || prev.notes,
        image: data.image || prev.image,
        priceRange: data.priceRange || prev.priceRange,
        isWishlist: true,
      }));
      try {
        const area = await normalizeAreaRemote({
          name: data.name || formData.name,
          location: data.location || formData.location,
          prefecture: data.prefecture || formData.prefecture,
          municipality: data.municipality || formData.municipality,
        });
        setFormData((prev) => ({
          ...prev,
          prefecture: area.prefecture || prev.prefecture,
          municipality: area.municipality || prev.municipality,
        }));
      } catch {
        // 地域補正は任意。抽出結果はそのまま残す。
      }
    } catch (e) { console.error(e as any); const msg = (e as any)?.message ? String((e as any).message) : "解析に失敗しました。URLをご確認ください。"; alert(msg); } finally {
      setParsing(false);
    }
  };

return (
    <div className="min-h-screen bg-background p-3 sm:p-4">
      <div className="w-full">
        <Card className="border-0 shadow-warm bg-card">
          <div className="p-4 sm:p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-gradient-primary">
                <Utensils className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-card-foreground">
                  {mode === "edit" ? "お店を編集" : "お店を追加"}
                </h1>
                <p className="text-muted-foreground">
                  {mode === "edit" ? "詳細を更新できます" : "食事体験を共有しましょう"}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* URL解析（保存しない） */}
              <div className="space-y-2">
                <Label htmlFor="parse-url" className="text-card-foreground font-medium">
                  お店のURLから自動入力
                </Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="parse-url"
                      placeholder="https://..."
                      value={parseUrl}
                      onChange={(e) => setParseUrl(e.target.value)}
                      className="pl-10 border-input"
                    />
                  </div>
                  <Button type="button" onClick={handleParseUrl} disabled={!parseUrl || parsing} className="sm:w-auto">
                    {parsing ? "解析中…" : "読み取る"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  食べログ、Googleマップ、公式サイト、記事、SNS投稿などのURLを貼ると、AIが店名/料理/場所/価格帯などを拾います。
                </p>
              </div>

              {/* 保存用URL（レコードに保持） */}
              <div className="space-y-2">
                <Label htmlFor="source-url" className="text-card-foreground font-medium">
                  保存用URL（メモとして保持・任意）
                </Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    id="source-url"
                    placeholder="https://..."
                    value={sourceUrl}
                    onChange={(e) => setSourceUrl(e.target.value)}
                    className="border-input flex-1"
                  />
                  {!sourceUrl && formData.name && (
                    <Button type="button" variant="outline" onClick={openTabelogSearch} title="店名＋エリアで食べログを検索">
                      食べログで検索
                    </Button>
                  )}
                </div>
                {!sourceUrl && formData.name && (
                  <p className="text-xs text-muted-foreground">店名とエリアから食べログを検索します（新しいタブで開きます）。</p>
                )}
              </div>

              {/* TikTok URL */}
              <div className="space-y-2">
                <Label htmlFor="tiktok-url" className="text-card-foreground font-medium">
                  TikTok投稿URL（任意）
                </Label>
                <Input
                  id="tiktok-url"
                  placeholder="https://www.tiktok.com/..."
                  value={tiktokUrl}
                  onChange={(e) => setTiktokUrl(sanitizeUrlInput(e.target.value))}
                  className="border-input"
                />
              </div>

              {/* Restaurant Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-card-foreground font-medium">
                  店名 *
                </Label>
                <Input
                  id="name"
                  placeholder="店名を入力"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="border-input focus:ring-primary"
                />
              </div>

              {/* Cuisine & Location Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cuisine" className="text-card-foreground font-medium">
                    料理の種類 *
                  </Label>
                  <Select
                    value={formData.cuisine}
                    onValueChange={(value) => setFormData({ ...formData, cuisine: value })}
                  >
                    <SelectTrigger className="border-input">
                      <SelectValue placeholder="料理を選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {cuisineTypes.slice().sort(compareDropdown).map((cuisine) => (
                        <SelectItem key={cuisine} value={cuisine}>
                          {cuisine}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location" className="text-card-foreground font-medium">
                    場所 *
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="location"
                      placeholder="市区町村・エリア"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      required
                      className="pl-10 border-input focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Prefecture & Municipality */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prefecture" className="text-card-foreground font-medium">
                    都道府県
                  </Label>
                  {areaOptions.length > 0 ? (
                    <Select
                      value={formData.prefecture || undefined}
                      onValueChange={(value) => setFormData({ ...formData, prefecture: value, municipality: "" })}
                    >
                      <SelectTrigger id="prefecture" className="border-input">
                        <SelectValue placeholder="都道府県を選択" />
                      </SelectTrigger>
                      <SelectContent>
                        {areaOptions.map((pref) => (
                          <SelectItem key={pref.pref} value={pref.pref}>{pref.pref}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="prefecture"
                      placeholder="東京都 / 大阪府 など"
                      value={formData.prefecture}
                      onChange={(e) => setFormData({ ...formData, prefecture: e.target.value })}
                      className="border-input"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="municipality" className="text-card-foreground font-medium">
                    市区町村
                  </Label>
                  {areaOptions.length > 0 && formData.prefecture ? (
                    <Select
                      value={formData.municipality || undefined}
                      onValueChange={(value) => setFormData({ ...formData, municipality: value })}
                    >
                      <SelectTrigger id="municipality" className="border-input">
                        <SelectValue placeholder="市区町村を選択" />
                      </SelectTrigger>
                      <SelectContent>
                        {(areaOptions.find((pref) => pref.pref === formData.prefecture)?.cities || []).map((city) => (
                          <SelectItem key={`${city.code}-${city.city}`} value={city.city}>{city.city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="municipality"
                      placeholder="渋谷区 / 横浜市西区 など"
                      value={formData.municipality}
                      onChange={(e) => setFormData({ ...formData, municipality: e.target.value })}
                      className="border-input"
                    />
                  )}
                </div>
              </div>

              {/* Wishlist Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                <div className="flex items-center gap-3">
                  <Heart
                    className={cn(
                      "h-5 w-5 transition-colors",
                      formData.isWishlist ? "text-accent fill-accent" : "text-muted-foreground"
                    )}
                  />
                  <div>
                    <Label htmlFor="wishlist" className="text-card-foreground font-medium">
                      行きたいリストに追加
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {formData.isWishlist ? "行ってみたいお店" : "訪問済みのお店"}
                    </p>
                  </div>
                </div>
                <Switch
                  id="wishlist"
                  checked={formData.isWishlist}
                  onCheckedChange={(checked) => setFormData({ ...formData, isWishlist: checked })}
                />
              </div>

              {/* Visit Date only (総合星は廃止) */}
              {!formData.isWishlist && (
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label className="text-card-foreground font-medium">訪問日</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal border-input",
                            !formData.visitDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.visitDate
                            ? format(formData.visitDate, "PPP", { locale: ja })
                            : "日付を選択"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.visitDate}
                          onSelect={(date) => setFormData({ ...formData, visitDate: date ?? undefined })}
                          initialFocus
                          className="p-3 pointer-events-auto"
                          locale={ja as any}
                        />
                      </PopoverContent>
                    </Popover>
                    <div className="flex gap-2 mt-2">
                      <Button
                        type="button"
                        onClick={() => {
                          if (!formData.visitDate || !isValid(formData.visitDate)) return;
                          const s = format(formData.visitDate, "yyyy年M月d日", { locale: ja as any });
                          setVisitHistory((prev) => {
                            const next = [s, ...prev.filter((x) => x !== s)];
                            return next.slice(0, 100);
                          });
                        }}
                      >
                        履歴に追加
                      </Button>
                    </div>
                    {visitHistory.length > 0 && (
                      <div className="space-y-1 mt-2">
                        {visitHistory.map((d, idx) => (
                          <div key={d+idx} className="flex items-center justify-between text-sm text-muted-foreground border rounded px-2 py-1">
                            <span>{d}</span>
                            <button
                              type="button"
                              className="text-xs underline"
                              onClick={() => setVisitHistory((prev) => prev.filter((x) => x !== d))}
                            >
                              削除
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 評価の内訳（訪問済みのみ） */}
              {!formData.isWishlist && (
                <div className="space-y-4">
                  <div className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <Label className="text-card-foreground font-medium flex items-center gap-2">味</Label>
                      <div className="text-xs sm:text-sm text-muted-foreground sm:text-right">
                        {tasteSummary.average > 0
                          ? `平均 ${tasteSummary.average.toFixed(1)} / 5`
                          : "未評価"}
                        {tasteSummary.fine > 0 && ` ・9段階 ${tasteSummary.fine.toFixed(1)} / 9`}
                        {tasteSummary.grade && ` ・グレード ${tasteSummary.grade}`}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <div className="text-xs font-medium text-muted-foreground">料理のバランス</div>
                        <Rating value={formData.ratingTasteBalance} onChange={(v) => setFormData({ ...formData, ratingTasteBalance: v })} />
                      </div>
                      <div className="space-y-1.5">
                        <div className="text-xs font-medium text-muted-foreground">素材の良さ</div>
                        <Rating value={formData.ratingTasteIngredients} onChange={(v) => setFormData({ ...formData, ratingTasteIngredients: v })} />
                      </div>
                      <div className="space-y-1.5">
                        <div className="text-xs font-medium text-muted-foreground">調理の丁寧さ</div>
                        <Rating value={formData.ratingTasteTechnique} onChange={(v) => setFormData({ ...formData, ratingTasteTechnique: v })} />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      サブ指標の評価から味の総合スコアとグレードを自動計算します。迷ったら素直な直感でどうぞ。
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-card-foreground font-medium">雰囲気</Label>
                      <Rating value={formData.ratingAmbience} onChange={(v) => setFormData({ ...formData, ratingAmbience: v })} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-card-foreground font-medium">接客</Label>
                      <Rating value={formData.ratingService} onChange={(v) => setFormData({ ...formData, ratingService: v })} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-card-foreground font-medium">価格（満足度）</Label>
                      <Rating value={formData.ratingCost} onChange={(v) => setFormData({ ...formData, ratingCost: v })} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-card-foreground font-medium">味（手動微調整）</Label>
                      <Rating value={formData.ratingTaste} onChange={(v) => setFormData({ ...formData, ratingTaste: v })} />
                      <p className="text-xs text-muted-foreground">
                        サブ指標だけで足りないときの微調整用。未入力でもOKです。
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-card-foreground font-medium">
                  メモ・レビュー
                </Label>
                <Textarea
                  id="notes"
                  placeholder={
                    formData.isWishlist
                      ? "なぜ行ってみたいのか（雰囲気・料理・価格帯など）"
                      : "感想・好きな料理・雰囲気など"
                  }
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="border-input focus:ring-primary resize-none"
                />
              </div>

              {/* 価格帯 */}
              <div className="space-y-2">
                <Label htmlFor="priceRange" className="text-card-foreground font-medium">
                  価格帯（例: ¥¥ / ¥1000-2000）
                </Label>
                <Input
                  id="priceRange"
                  placeholder="¥¥ / ¥1000-2000 など"
                  value={formData.priceRange}
                  onChange={(e) => setFormData({ ...formData, priceRange: e.target.value })}
                  className="border-input"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={onCancel} className="flex-1 border-input">
                  キャンセル
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-primary hover:shadow-soft transition-all duration-300"
                  disabled={!formData.name || !formData.cuisine || !formData.location}
                >
                  {formData.isWishlist ? "行きたいに追加" : "保存"}
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default AddRestaurantForm;



